import { useState, useEffect } from "react";
import { X, AlertTriangle } from "lucide-react";
import { useSettings, getSettingWithFallback } from "@/hooks/use-cms";
import { getTopBarsHint } from "@/lib/top-bars-hint";

// The primary visibility hint is server-injected (window.__UO_TOP_BARS__ —
// deterministic on every load, first visit included). The localStorage key
// remains as a fallback for the rare case the inline script is missing.
const BAR_ACTIVE_HINT_KEY = "uo_announcement_bar_active";

export function AnnouncementBar() {
  const [isDismissed, setIsDismissed] = useState(false);
  const [reserveSpace] = useState<boolean>(() => {
    const hint = getTopBarsHint();
    if (hint) return hint.announcementBar;
    try {
      return localStorage.getItem(BAR_ACTIVE_HINT_KEY) === "true";
    } catch {
      return false;
    }
  });
  
  const { data: settings, isLoading } = useSettings([
    'announcement_bar_enabled',
    'announcement_title',
    'announcement_message',
  ]);

  const barEnabledValue: string = getSettingWithFallback(settings, 'announcement_bar_enabled', '');
  const isEnabled = barEnabledValue === 'true';
  const title = getSettingWithFallback(settings, 'announcement_title', '');
  const message = getSettingWithFallback(settings, 'announcement_message', '');

  const shouldShow = isEnabled && !!message;

  // Keep the hint in sync once the settings have loaded.
  useEffect(() => {
    if (isLoading) return;
    try {
      if (shouldShow) {
        localStorage.setItem(BAR_ACTIVE_HINT_KEY, "true");
      } else {
        localStorage.removeItem(BAR_ACTIVE_HINT_KEY);
      }
    } catch {
      // localStorage unavailable — skip the hint
    }
  }, [isLoading, shouldShow]);

  // While loading: if the bar was visible on the last visit, reserve its exact
  // height (h-9 = py-2 + text-sm line) so content below doesn't jump.
  if (isLoading) {
    if (reserveSpace && !isDismissed) {
      return <div className="w-full h-9 bg-[#e0ae26]" aria-hidden="true" data-testid="bar-announcement-placeholder" />;
    }
    return null;
  }

  if (!shouldShow || isDismissed) return null;

  const displayText = title ? `${title}: ${message}` : message;

  return (
    <div 
      className="w-full h-9 flex items-center bg-[#e0ae26] text-white px-4 relative z-50"
      data-testid="bar-announcement"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <p className="text-sm font-medium text-center truncate">
          {displayText}
        </p>
        <button
          onClick={() => setIsDismissed(true)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/20 rounded transition-colors"
          aria-label="Chiudi avviso"
          data-testid="button-announcement-bar-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
